/**
 * Participant Active Session API Service (MED-805 / E3.3).
 *
 * Backs the participant active session screen. Gated by the
 * `live_session_delivery` feature flag at the API. The page polls
 * `getActiveState` at ~2s cadence so participant content keeps up with the
 * session leader's actions within the AC #6 SLA.
 */

import apiClient from './client';
import type { PresenceMode } from './waitingRoom';

// ============================================================================
// Types — mirror medley-api/types/participantActiveSession.ts
// ============================================================================

import type { AuthoredAgendaSectionForParticipant } from './coachSessionPrep';
export type { AuthoredAgendaSectionForParticipant } from './coachSessionPrep';

export type ParticipantActiveSessionStatus =
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'rescheduled';

export interface ParticipantActiveSessionLeader {
  user_id: string | null;
  display_name: string;
  first_name: string;
  role_label: 'COACH' | 'PEER LEADER' | null;
}

export interface ParticipantActiveSessionSlideDeck {
  id: string;
  converted_pdf_s3_key: string | null;
  processing_state: 'pending' | 'processing' | 'ready' | 'failed';
  original_filename: string;
}

export interface ParticipantActiveSessionState {
  session_id: string;
  title: string;
  session_number: number | null;
  // MED-1180 — per-cohort LSD gate. Client defense-in-depth: render
  // FeaturesDisabledPage when false (backend also hard-404s the route).
  live_session_delivery_enabled: boolean;
  status: ParticipantActiveSessionStatus;
  duration_minutes: number | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  cohort_id: string;
  cohort_name: string;
  /**
   * MED-1112 — resolved meeting link (Zoom/Teams/Meet), or null when there's
   * none (in-person). Surfaced as the top-bar "Join video call" button.
   */
  meeting_link: string | null;
  group_id: string | null;
  group_name: string | null;
  modality_id: string | null;
  coaching_sub_type: 'coach_led' | 'peer_led' | null;
  ai_features_enabled: boolean;
  /** MED-876 — per-session AI Listening toggle. AI listening pill gates on BOTH flags. */
  ai_listening_enabled: boolean;
  leader: ParticipantActiveSessionLeader;
  /**
   * MED-976 (E1.3h) — Authored sections (slim, no leader_prompts — AC-PL3).
   * The participant active route is already gated behind `live_session_delivery`
   * (MED-886) so this field is always the authored shape — no legacy fallback
   * at the type level.
   */
  sections: AuthoredAgendaSectionForParticipant[];
  slide_deck: ParticipantActiveSessionSlideDeck | null;
  current_section_index: number | null;
  /**
   * MED-976/E3.3a — the coach-selected "main" slide for the shared whiteboard
   * (1-based PDF page). NULL ⇒ the current section's first associated slide.
   * Read-only for participants.
   */
  current_slide_page: number | null;
  /**
   * MED-983 (E3.3a) — TRUE when the participant should see the commitment-entry
   * field. Semantics changed from the MED-805/MED-976 title regex to the
   * authored per-section `show_commitment_field` flag, additionally gated on
   * the cohort's `commitment_tracking_enabled` setting.
   */
  commitment_section_active: boolean;
  /**
   * MED-983 (E3.3a / IV4) — Cohort-level commitment tracking gate. When false,
   * the participant never sees the entry field or the prior-commitment display
   * regardless of per-section flags.
   */
  commitment_tracking_enabled: boolean;
  /**
   * MED-983 (E3.3a / AC-SLC) — The participant's commitment from their prior
   * session (same group, session_number - 1). Non-null ONLY when the cohort
   * gate is on, the current section's `show_last_session_commitment` is true,
   * and a prior commitment exists. Null otherwise (first session, no prior
   * entry, etc.).
   */
  last_session_commitment: string | null;
  /**
   * MED-952 AC8/AC9 — false when no assigned coach is currently present. Drives
   * the "coach has temporarily stepped away" notice; flips back to true within
   * one poll when a coach rejoins.
   */
  coach_present: boolean;
}

export interface ParticipantNotesResponse {
  notes_html: string;
  updated_at: string | null;
}

export interface ParticipantCommitmentResponse {
  commitment_text: string;
  updated_at: string | null;
  /**
   * MED-1216 / AC8 — true when the coach entered this and the participant has
   * not yet saved their own. Drives the "noted by your coach, please review and
   * update if needed" review state. Always false after a self-save.
   */
  authored_by_coach: boolean;
}

// ============================================================================
// API methods
// ============================================================================

export const participantActiveSessionApi = {
  /** MDF-242: same presence modes as waitingRoomApi.get. */
  getActiveState: (
    sessionId: string,
    opts: { presence?: PresenceMode } = {}
  ): Promise<ParticipantActiveSessionState> =>
    apiClient.get<ParticipantActiveSessionState>(
      `/api/participant/sessions/${sessionId}/active-state`,
      opts.presence === 'unless_ai_disclosure' ? { presence: opts.presence } : undefined
    ),

  getNotes: (sessionId: string): Promise<ParticipantNotesResponse> =>
    apiClient.get<ParticipantNotesResponse>(
      `/api/participant/sessions/${sessionId}/notes`
    ),

  putNotes: (
    sessionId: string,
    notesHtml: string
  ): Promise<ParticipantNotesResponse> =>
    apiClient.put<ParticipantNotesResponse>(
      `/api/participant/sessions/${sessionId}/notes`,
      {
        notes_html: notesHtml,
      }
    ),

  getCommitment: (sessionId: string): Promise<ParticipantCommitmentResponse> =>
    apiClient.get<ParticipantCommitmentResponse>(
      `/api/participant/sessions/${sessionId}/commitment`
    ),

  putCommitment: (
    sessionId: string,
    commitmentText: string
  ): Promise<ParticipantCommitmentResponse> =>
    apiClient.put<ParticipantCommitmentResponse>(
      `/api/participant/sessions/${sessionId}/commitment`,
      { commitment_text: commitmentText }
    ),
};
